import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { useInAppWindowFocus } from "@/lib/inAppWindowFocus";
import { MODAL_STACK_GROUP_TIMESHEET, ModalStackGroup } from "@/lib/modalStack";
import { TimesheetActionsModal } from "@/features/timesheetFlow/components/TimesheetActionsModal";
import { TimesheetFlowFooterButton } from "@/features/timesheetFlow/components/TimesheetFlowFooter";
import { CompletionEditorScreen } from "@/features/timesheetFlow/screens/CompletionEditorScreen";
import { LieferscheinDetailPanel } from "@/features/timesheetFlow/screens/LieferscheinDetailPanel";
import { LieferscheinEditorScreen } from "@/features/timesheetFlow/screens/LieferscheinEditorScreen";
import { MischmaschChoiceScreen } from "@/features/timesheetFlow/screens/MischmaschChoiceScreen";
import { MuendlichBeauftragtScreen } from "@/features/timesheetFlow/screens/MuendlichBeauftragtScreen";
import { ProjectPickerScreen } from "@/features/timesheetFlow/screens/ProjectPickerScreen";
import { TimesheetDetailPanel } from "@/features/timesheetFlow/screens/TimesheetDetailPanel";
import { TimesheetEditorScreen } from "@/features/timesheetFlow/screens/TimesheetEditorScreen";
import {
  createScreenForAuftragsartRoute,
  resolveAuftragsartRoute,
  resolveProjectAuftragsart,
} from "@/features/timesheetFlow/lib/auftragsart";
import { resolveTimesheetPermissions, type TimesheetPermissions } from "@/features/timesheetFlow/timesheetPermissions";
import "@/features/timesheetFlow/timesheetFlow.css";
import type {
  AuftragsartRoute,
  TimesheetDetailTab,
  TimesheetFlowScreen,
  TimesheetProjectPreset,
} from "@/features/timesheetFlow/timesheetFlowTypes";
import { getProjectCustomFieldSchemas, type Order } from "@/lib/projects";
import { useCurrentUser } from "@/lib/auth";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";

export interface TimesheetFlowHostProps {
  open: boolean;
  screen: TimesheetFlowScreen;
  project: TimesheetProjectPreset | null;
  timesheetId: number | null;
  detailInitialTab?: TimesheetDetailTab | null;
  auftragsartValue: string | null;
  onScreenChange: (screen: TimesheetFlowScreen) => void;
  onProjectChange: (project: TimesheetProjectPreset | null) => void;
  onTimesheetIdChange: (id: number | null) => void;
  onAuftragsartChange: (value: string | null, route: AuftragsartRoute | null) => void;
  lieferscheinId: number | null;
  onClose: () => void;
  onSaved: () => void;
  onShowLieferscheinDetail: (dokumentId: number) => void;
  onPatchListAtContact?: (timesheetId: number, atContact: boolean) => void;
}

function orderToPreset(o: Order): TimesheetProjectPreset {
  return {
    id: o.id,
    auftragsnummer: o.auftragsnummer,
    jahr: o.jahr,
    kunde: o.kunde,
    ort: o.ort,
    ort_zusatz: o.ort_zusatz,
    hero_project_id: o.hero_project_id,
    kunden_id: o.kunden_id,
    ordner_id: o.ordner_id,
    sordner_id: o.sordner_id,
    auftragsart_app: o.auftragsart_app,
    hero_custom_fields: o.hero_custom_fields,
  };
}

export function TimesheetFlowHost({
  open,
  screen,
  project,
  timesheetId,
  detailInitialTab,
  auftragsartValue,
  onScreenChange,
  onProjectChange,
  onTimesheetIdChange,
  onAuftragsartChange,
  lieferscheinId,
  onClose,
  onSaved,
  onShowLieferscheinDetail,
  onPatchListAtContact,
}: TimesheetFlowHostProps) {
  const { user } = useCurrentUser();
  const { focusSurface } = useInAppWindowFocus();
  const layers = useTimesheetFlowLayers();
  const [permissions, setPermissions] = useState<TimesheetPermissions | null>(null);
  const [unknownConfirm, setUnknownConfirm] = useState(false);
  const [actionsTimesheetId, setActionsTimesheetId] = useState<number | null>(null);

  useEffect(() => {
    void resolveTimesheetPermissions(user).then(setPermissions);
  }, [user]);

  useEffect(() => {
    // Bestehender Nachweis (Entwurf/Bearbeiten) ist bereits ein Stundennachweis.
    if (!open || timesheetId != null) {
      setUnknownConfirm(false);
      return;
    }
    if (
      screen === "timesheetEditor" &&
      resolveAuftragsartRoute(auftragsartValue) === "unknown"
    ) {
      setUnknownConfirm(true);
    }
  }, [open, screen, auftragsartValue, timesheetId]);

  const routeProject = useCallback(
    async (preset: TimesheetProjectPreset) => {
      onProjectChange(preset);
      const schemas = await getProjectCustomFieldSchemas().catch(() => []);
      const art = resolveProjectAuftragsart(preset, schemas);
      const route = resolveAuftragsartRoute(art);
      onAuftragsartChange(art, route);
      if (route === "unknown") {
        setUnknownConfirm(true);
        return;
      }
      onScreenChange(createScreenForAuftragsartRoute(route) ?? "timesheetEditor");
    },
    [onProjectChange, onAuftragsartChange, onScreenChange],
  );

  const title =
    screen === "projectPicker"
      ? "Projekt auswählen"
      : screen === "mischmaschChoice"
        ? "Nachweis + Festpreis"
        : screen === "completionEditor"
          ? "Fertigstellungsmeldung"
          : screen === "muendlichBeauftragt"
            ? "Mündlich beauftragt"
              : screen === "lieferscheinEditor" || screen === "lieferscheinDetail"
              ? "Lieferschein"
              : screen === "timesheetEditor"
                ? "Stundennachweis"
                : screen === "detail"
                  ? "Stundennachweis"
                  : "Nachweis";

  const dialogSize =
    screen === "projectPicker" ||
    screen === "mischmaschChoice" ||
    screen === "completionEditor" ||
    screen === "muendlichBeauftragt"
      ? "md"
      : screen === "lieferscheinEditor"
        ? "2xl"
        : screen === "detail" || screen === "lieferscheinDetail"
          ? "3xl"
          : screen === "timesheetEditor"
            ? "3xl"
            : "xl";

  const handleSaved = (id: number) => {
    onTimesheetIdChange(id);
    onSaved();
  };

  const handleOpenActions = (id: number) => {
    handleSaved(id);
    setActionsTimesheetId(id);
  };

  const handleCloseActions = () => {
    setActionsTimesheetId(null);
    onClose();
  };

  const handleOpenLieferscheinDetail = (dokumentId: number) => {
    onSaved();
    onShowLieferscheinDetail(dokumentId);
  };

  const detailScreen = screen === "detail" || screen === "lieferscheinDetail";

  return (
    <ModalStackGroup group={MODAL_STACK_GROUP_TIMESHEET}>
      <Dialog
        open={open && actionsTimesheetId == null}
        onClose={onClose}
        // Editor-Screens halten ungespeicherte Eingaben – Schließen bestätigen lassen.
        dirty={
          screen === "timesheetEditor" ||
          screen === "completionEditor" ||
          screen === "muendlichBeauftragt" ||
          screen === "lieferscheinEditor"
        }
        title={detailScreen ? undefined : title}
        size={dialogSize}
        zIndexClass={layers.flow}
        onActivate={() => focusSurface("timesheet")}
        fullScreenMobile
        panelClassName={
          screen === "detail" ||
          screen === "lieferscheinDetail" ||
          screen === "timesheetEditor" ||
          screen === "lieferscheinEditor"
            ? "flex flex-col sm:h-[min(92vh,56rem)] sm:max-h-[min(92vh,56rem)]"
            : undefined
        }
        bodyClassName={clsx(
          "timesheet-flow-shell",
          detailScreen
            ? "flex min-h-0 flex-col p-0"
            : screen === "timesheetEditor" || screen === "lieferscheinEditor"
              ? "flex min-h-0 flex-1 flex-col py-3 max-sm:py-2"
              : "max-sm:py-3",
        )}
      >
        {!permissions?.canManage && !detailScreen ? (
          <p className="text-sm text-status-red">Keine Berechtigung zum Anlegen von Nachweisen.</p>
        ) : null}

        {screen === "projectPicker" ? (
          <ProjectPickerScreen
            onSelect={(p) => void routeProject(p)}
            onCancel={onClose}
          />
        ) : null}

        {screen === "mischmaschChoice" && project ? (
          <MischmaschChoiceScreen
            project={project}
            onTimesheet={() => onScreenChange("timesheetEditor")}
            onBack={() => onScreenChange("projectPicker")}
          />
        ) : null}

        {screen === "completionEditor" && project ? (
          <CompletionEditorScreen
            project={project}
            onDone={() => {
              onSaved();
              onClose();
            }}
            onBack={() => {
              if (auftragsartValue && resolveAuftragsartRoute(auftragsartValue) === "mischmasch") {
                onScreenChange("mischmaschChoice");
              } else {
                onScreenChange("projectPicker");
              }
            }}
          />
        ) : null}

        {screen === "muendlichBeauftragt" && project ? (
          <MuendlichBeauftragtScreen
            project={project}
            onDone={() => {
              onSaved();
              onClose();
            }}
            onBack={onClose}
          />
        ) : null}

        {screen === "lieferscheinEditor" && project ? (
          <LieferscheinEditorScreen
            project={project}
            onDone={(dokumentId) => handleOpenLieferscheinDetail(dokumentId)}
            onOpenExisting={(dokumentId) => handleOpenLieferscheinDetail(dokumentId)}
            onBack={() => {
              if (auftragsartValue && resolveAuftragsartRoute(auftragsartValue) === "mischmasch") {
                onScreenChange("mischmaschChoice");
              } else {
                onScreenChange("projectPicker");
              }
            }}
          />
        ) : null}

        {screen === "timesheetEditor" && project ? (
          <TimesheetEditorScreen
            project={project}
            timesheetId={timesheetId}
            auftragsart={auftragsartValue}
            onSaved={(id) => {
              handleSaved(id);
              // Entwurf: Dialog schließen – Detail erst nach Fertigstellung (Actions-Modal).
              onClose();
            }}
            onOpenActions={handleOpenActions}
            onOpenDetail={(id) => {
              onTimesheetIdChange(id);
              onScreenChange("detail");
            }}
            onBack={() => {
              if (timesheetId) {
                onScreenChange("detail");
              } else if (auftragsartValue && resolveAuftragsartRoute(auftragsartValue) === "mischmasch") {
                onScreenChange("mischmaschChoice");
              } else {
                onScreenChange("projectPicker");
              }
            }}
            onCancel={onClose}
            onChangeProject={
              timesheetId
                ? undefined
                : () => {
                    onTimesheetIdChange(null);
                    onScreenChange("projectPicker");
                  }
            }
          />
        ) : null}

        {screen === "detail" && timesheetId && permissions ? (
          <TimesheetDetailPanel
            timesheetId={timesheetId}
            permissions={permissions}
            initialTab={detailInitialTab ?? undefined}
            onEdit={() => onScreenChange("timesheetEditor")}
            onRevision={(newId) => {
              onSaved();
              onTimesheetIdChange(newId);
              onScreenChange("timesheetEditor");
            }}
            onRefresh={onSaved}
            onPatchListAtContact={onPatchListAtContact}
            onClose={onClose}
          />
        ) : null}

        {screen === "lieferscheinDetail" && lieferscheinId && project ? (
          <LieferscheinDetailPanel
            auftragId={project.id}
            dokumentId={lieferscheinId}
            onClose={onClose}
            onRefresh={onSaved}
          />
        ) : null}
      </Dialog>

      <TimesheetActionsModal
        open={actionsTimesheetId != null}
        timesheetId={actionsTimesheetId}
        permissions={permissions}
        onClose={handleCloseActions}
        onRefresh={onSaved}
      />

      <Dialog
        open={unknownConfirm}
        onClose={() => setUnknownConfirm(false)}
        title="Auftragsart prüfen"
        size="sm"
        zIndexClass={layers.overlay}
        fullScreenMobile
        bodyClassName="timesheet-flow-shell"
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <TimesheetFlowFooterButton type="button" variant="secondary" onClick={() => setUnknownConfirm(false)}>
              Abbrechen
            </TimesheetFlowFooterButton>
            <TimesheetFlowFooterButton
              type="button"
              variant="secondary"
              onClick={() => {
                setUnknownConfirm(false);
                onScreenChange("lieferscheinEditor");
              }}
            >
              Lieferschein schreiben
            </TimesheetFlowFooterButton>
            <TimesheetFlowFooterButton
              type="button"
              onClick={() => {
                setUnknownConfirm(false);
                onScreenChange("timesheetEditor");
              }}
            >
              Stundennachweis
            </TimesheetFlowFooterButton>
          </div>
        }
      >
        <p className="text-sm text-label-secondary">
          Für diesen Auftrag konnte keine Auftragsart ermittelt werden. Bei Festpreis bitte einen
          Lieferschein schreiben, sonst einen Stundennachweis.
        </p>
      </Dialog>
    </ModalStackGroup>
  );
}

export { orderToPreset };
